import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import { useAuth } from "@/src/contexts/auth.context";
import { BlocksApiArticle } from "@/src/models/wiki.models";
import {
  useToggleWikiSaved,
  useWikiDeleteArticle,
  useWikiSaveState,
} from "@/src/services/wiki/wiki.hooks";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Image, Text, useWindowDimensions, View } from "react-native";
import WikiArticleCard from "../wiki-article-card/wiki-article-card";
import WikiCardSkeleton from "../wiki-card-skeleton";

const BREAKPOINT_LG = 1024;

type WikiSavedCardProps = Readonly<{
  article: BlocksApiArticle;
  onUnsave: (id: string) => void;
  onDelete: (id: string) => void;
  itemWidthClass: string;
  onPress?: (article: BlocksApiArticle) => void;
}>;

function WikiSavedCard({
  article,
  onUnsave,
  onDelete,
  itemWidthClass,
  onPress,
}: WikiSavedCardProps) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const dropdownItems = [
    { label: t("BTN_UNSAVE"), onPress: () => onUnsave(article.ItemId) },
    { label: t("LABEL_SHARE"), onPress: () => setShowShareModal(true) },
    { label: t("BTN_UPDATE"), onPress: () => handleEdit() },
    {
      label: t("BTN_DELETE"),
      onPress: () => setShowDeleteConfirm(true),
      destructive: true,
    },
  ];

  const handleEdit = () => {
    router.push({
      pathname: "/(tabs)/wiki/wiki-creation",
      params: {
        draftId: article.ItemId,
        Title: article.Title,
        Content: article.Content,
        categoryId: article.categoryIds ?? "",
        coverImageUrl: article.coverImageUrl ?? "",
        attachments: JSON.stringify(
          (article.attachmentUrls ?? []).map((att) => ({
            id: att.id,
            name: att.name,
            size: att.size,
            url: att.url,
            mimeType: att.mimeType,
          })),
        ),
      },
    });
  };

  return (
    <>
      <ConfirmModal
        visible={showDeleteConfirm}
        title="BTN_DELETE"
        description="LABEL_DESC_DELETE_WIKI"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={() => {
          onDelete(article.ItemId);
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      <WikiArticleCard
        article={article}
        itemWidthClass={itemWidthClass}
        dropdownItems={dropdownItems}
        onPress={onPress}
        showShareModal={showShareModal}
        onShareModalClose={() => setShowShareModal(false)}
      />
    </>
  );
}

export default function WikiSaved({
  searchText = "",
  selectedCategories = [],
  onArticlePress,
}: {
  searchText?: string;
  selectedCategories?: string[];
  onArticlePress?: (article: BlocksApiArticle) => void;
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  // Small screens show 2 cards per row (never 1); large → 3.
  const numColumns = width >= BREAKPOINT_LG ? 3 : width >= 768 ? 2 : 1;
  const itemWidthClass =
    numColumns === 3 ? "w-[32.4%]" : numColumns === 2 ? "w-[49%]" : "w-full";
  const columnWrapperStyle = numColumns > 1 ? { gap: 10 } : undefined;
  const contentContainerStyle = { gap: 12 };

  const { savedArticles, isLoadingSavedArticles } =
    useWikiSaveState(searchText);
  const { mutate: toggleSave } = useToggleWikiSaved(userId);
  const { mutate: deleteArticle } = useWikiDeleteArticle();

  const [localDeletedIds, setLocalDeletedIds] = useState<string[]>([]);
  const [localUnsavedIds, setLocalUnsavedIds] = useState<string[]>([]);

  const handleUnsave = (id: string) => {
    setLocalUnsavedIds((prev) => [...prev, id]);
    toggleSave(id);
  };

  const handleDelete = (id: string) => {
    setLocalDeletedIds((prev) => [...prev, id]);
    deleteArticle(id);
  };

  const filteredArticles = savedArticles.filter((article) => {
    if (localDeletedIds.includes(article.ItemId)) return false;
    if (localUnsavedIds.includes(article.ItemId)) return false;
    if (selectedCategories.length === 0) return true;
    return selectedCategories.includes(article.categoryIds ?? "");
  });

  if (isLoadingSavedArticles) {
    return (
      <View className="flex-1">
        <View className="flex-row flex-wrap" style={{ gap: 10 }}>
          {Array.from({ length: numColumns * 2 }).map((_, index) => (
            <WikiCardSkeleton key={index} className={itemWidthClass} />
          ))}
        </View>
      </View>
    );
  }

  if (!filteredArticles.length) {
    return (
      <View className="flex-1 items-center justify-center gap-4">
        <Image
          source={require("@/src/assets/images/empty-state.png")}
          style={{ width: 100, height: 100 }}
          resizeMode="contain"
        />
        <Text className="text-ash-1 text-sm">
          {searchText.trim() || selectedCategories.length > 0
            ? t("HINT_WIKI_FILTER")
            : t("LABEL_NO_ARTICLES")}
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1">
      <FlatList
        key={numColumns}
        data={filteredArticles}
        keyExtractor={(article) => article.ItemId}
        numColumns={numColumns}
        columnWrapperStyle={columnWrapperStyle}
        contentContainerStyle={contentContainerStyle}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <WikiSavedCard
            article={item}
            onUnsave={handleUnsave}
            onDelete={handleDelete}
            itemWidthClass={itemWidthClass}
            onPress={onArticlePress}
          />
        )}
      />
    </View>
  );
}
